import { Injectable } from '@angular/core';
import { AbstractEntityService } from '@app/shared/abstract/abstract-entity.service';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ResourceData } from '@app/shared/abstract/resource.data';
import { ProductModel } from './product.model';
import { RouteParams } from '@app/shared/abstract/route-params';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends AbstractEntityService<ProductModel> {
  constructor(http: HttpClient) {
    super('/api/products', http);
  }

  saveFormData(formData: FormData, productId?: number | null): Observable<ResourceData<ProductModel>> {
    if (productId) {
      return this.http.post<ResourceData<ProductModel>>(`${this.apiUrl}${this.basePath}/${productId}`, formData);
    } else {
      return this.http.post<ResourceData<ProductModel>>(`${this.apiUrl}${this.basePath}`, formData);
    }
  }

  /**
   * Lightweight search for dropdowns - only returns id, name, and price (no variants/options/images)
   */
  searchProducts(
    searchTerm: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: { minSpend?: boolean; qualifying?: boolean; offer?: boolean; categories?: string[] },
    availableOn?: string,
  ): Observable<{ options: { value: any; label: string; meta?: any }[], hasMore: boolean }> {
    let params: any = {
      pageNum: page,
      pageSize: pageSize
    };

    if (searchTerm) {
      params['filter[search]'] = searchTerm;
    }
    if (filters?.minSpend) {
      params['filter[is_minimum_spend]'] = true;
    }
    if (filters?.qualifying) {
      params['filter[counts_to_500_deal]'] = true;
    }
    if (filters?.offer) {
      params['filter[inc_in_150_free]'] = true;
    }
    if (filters?.categories && filters.categories.length > 0) {
      params['filter[categories]'] = filters.categories.join(',');
    }
    if (availableOn) {
      params['filter[available_on]'] = availableOn;
    }

    return this.http.get<any>(`${this.apiUrl}${this.basePath}/search-light`, { params }).pipe(
      map(response => ({
        options: response.data.map((product: any) => {
          const priceText = product.price_formatted ?? '£0.00';
          const badges: ('minSpend' | 'qualifying' | 'offer')[] = [];
          if (product.is_minimum_spend) {
            badges.push('minSpend');
          }
          if (product.counts_to_500_deal) {
            badges.push('qualifying');
          }
          if (product.inc_in_150_free) {
            badges.push('offer');
          }
          return {
            value: product.id,
            label: `${product.name} — ${priceText}`,
            meta: {
              name: product.name,
              price: priceText,
              badges,
              category: product.category,
            },
          };
        }),
        hasMore: response.meta.current_page < response.meta.last_page,
      }))
    );
  }

  getByIdForDate(id: number | string, availableOn: string): Observable<ResourceData<ProductModel>> {
    return this.http.get<ResourceData<ProductModel>>(
      `${this.apiUrl}${this.basePath}/${id}`,
      { params: { available_on: availableOn } }
    );
  }

  getDailyPrices(productId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}${this.basePath}/${productId}/prices`);
  }

  duplicate(productId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}${this.basePath}/${productId}/duplicate`, {});
  }

  getDuplicateStatus(batchId: string): Observable<{ status: string; batch_id: string; product_id?: number | null; message?: string }> {
    return this.http.get<{ status: string; batch_id: string; product_id?: number | null; message?: string }>(
      `${this.apiUrl}${this.basePath}/duplicate-status/${batchId}`,
    );
  }

  getUsage(
    productId: number,
  ): Observable<{ events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>; event_count: number }> {
    return this.http.get<{ events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>; event_count: number }>(
      `${this.apiUrl}${this.basePath}/${productId}/usage`,
    );
  }

  getDailyPriceForDate(productId: number, date: string): Observable<any> {
    return this.http.get(`${this.apiUrl}${this.basePath}/${productId}/prices/${date}`);
  }

  storeDailyPrice(productId: number, data: { date: string; price: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}${this.basePath}/${productId}/prices`, data);
  }

  storeBulkDailyPrices(productId: number, data: { start_date: string; end_date: string; price: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}${this.basePath}/${productId}/prices/bulk`, data);
  }

  deleteBulkDailyPrices(productId: number, data: { start_date: string; end_date: string }): Observable<any> {
    return this.http.request('delete', `${this.apiUrl}${this.basePath}/${productId}/prices/bulk`, { body: data });
  }

  deleteDailyPrice(productId: number, dailyPriceId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}${this.basePath}/${productId}/prices/${dailyPriceId}`);
  }
}
